'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { graphql } from 'react-relay';
import AsyncSelect from 'react-select/async';
import Icon from '@/core/icon.js';
import get from '@/helpers/getValue';
import { hocTranslateServerClient, lang } from '@/helpers/translate';
import HeroSearch from '@/components/home/heroSearch';
import Hubs from '@/components/fields/hubs.js';
import { useZusTrackerStore } from '@/helpers/tracker.js';
import { getPlaceData } from '@/helpers/position.js';
import cookie from '@/helpers/cookie';
import Button from '@/core/button';
import Calendar from '@/components/fields/calendar/index.js';
import { usePatternAutocomplete } from '@/helpers/usePatternAutocomplete';

export const querySearchPlaceId = graphql`
  query searchQuery ($place_id: String!) {
    res: geoGetByPlaceId(place_id: $place_id) {
      location {
        formatted_address
        lat
        lng
        viewport {
          northeast { lat lng }
          southwest { lat lng }
        }
      }
    }
  }
`;

const PATTERN_MENU_WIDTH = 288;

/*
Construit la route de la page de recherche depuis les refs Hubs / Calendar
et les mots-clés sélectionnés. Partagé entre la barre de recherche classique
et la barre de recherche de la homepage (components/home/heroSearch.js).
*/
export async function computeSearchRoute({
  refs, selectedPatterns, cityDefault, langCurrent,
}) {
  const hubDefault = get(refs, 'refHubs.current.getValueDefault')
    ? refs.refHubs.current.getValueDefault(cityDefault)
    : false;
  const hub = refs.refHubs.current?.getValue?.() || hubDefault;

  if (!hub) return null;

  let resPlace = hub;
  if (hub.gg_place_id) {
    resPlace = await getPlaceData({ place_id: hub.gg_place_id });
    if (!resPlace) return null;
  }

  const dateData = refs.refCal.current?.getOutput?.() || null;
  const searchurl = JSON.parse(cookie.get(`urlSearch${hub.slug}`) || 'null');
  const patternValues = (selectedPatterns || []).map(({ value }) => value).filter(Boolean).join(',');

  return {
    pathname: `${langCurrent === 'en' ? '' : `/${langCurrent}`}/s/[location]`,
    query: {
      location: searchurl?.hub || hub.slug,
      ...(searchurl || {
        title: resPlace.location.formatted_address,
        lat: resPlace.location.lat,
        lng: resPlace.location.lng,
        page: 1,
        hub: hub.slug,
      }),
      ...(dateData?.url ? { dates: dateData.url } : {}),
      ...(patternValues ? { pattern: patternValues } : {}),
    },
  };
}

/*
Résout quand la navigation est terminée : `router.push` (Pages Router) ne se
résout qu'une fois le getServerSideProps de la search reçu et la page rendue,
d'où le loader du bouton tenu jusque-là (une erreur résout `false`). Sans
router (App Router) on change de document : la promesse ne se résout jamais,
la page est déchargée.
*/
export function navigateToSearch({ router, routerData, langCurrent }) {
  return new Promise((resolve) => {
    setTimeout(() => {
      if (router) {
        router.push(routerData).then(resolve, () => resolve(false));
      } else {
        const { location, ...params } = routerData.query;
        const urlParams = new URLSearchParams(params).toString();
        window.location.href = `${langCurrent === 'en' ? '' : `/${langCurrent}`}/s/${location}?${urlParams}`;
      }
    }, 0);
  });
}

const selectClassNames = {
  control: () => '!bg-transparent !border-none !shadow-none !min-h-0 !cursor-text',
  valueContainer: () => '!p-0 !flex-wrap',
  input: () => '!text-white !m-0 !p-0 md:!text-lg !pl-0',
  placeholder: () => 'font-normal !text-purple-4 !m-0 md:!text-[16px] !mx-0 !absolute !left-0 !whitespace-nowrap !overflow-hidden !text-ellipsis !max-w-full',
  multiValue: () => '!bg-purple !rounded !mt-0.5',
  multiValueLabel: () => '!text-white !text-sm !px-2 !py-1 lg:!leading-4.5',
  multiValueRemove: () => '!text-white hover:!bg-purple-dark hover:!text-white !px-1',
  menu: () => '!bg-purple-6 !rounded-lg !shadow-button !z-50',
  menuList: () => '!p-2',
  option: (state) => (state.isFocused
    ? '!bg-purple !text-white !rounded !cursor-pointer !py-2 !px-3 !text-left'
    : '!bg-transparent !text-white !rounded !cursor-pointer !py-2 !px-3 hover:!bg-purple-5 !text-left'),
  noOptionsMessage: () => '!text-purple-4 !py-2 !text-left',
  indicatorsContainer: () => '!hidden',
};

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 'auto' }),
  valueContainer: (base) => ({ ...base, padding: 0, gap: '4px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menu: (base) => ({
    ...base,
    zIndex: 50,
    width: PATTERN_MENU_WIDTH,
    marginLeft: '-16px',
    marginTop: '24px',
  }),
};

function Search({
  isCms, isPopin, onClose, moreHtml = {}, cityDefault, t,
}) {
  const router = useRouter();
  const { loadOptions: loadPatternOptions } = usePatternAutocomplete();
  const { setOrEvent } = useZusTrackerStore.getState();
  const langCurrent = lang();

  // Refs
  const divRefs = {
    hubs: useRef(null),
    calendar: useRef(null),
    pattern: useRef(null),
  };
  const refs = {
    refHubs: useRef(),
    refCal: useRef(),
  };
  const patternSelectRef = useRef(null);

  // State
  const [clicked, setClicked] = useState({});
  const [calendarHovered, setCalendarHovered] = useState(false);
  const [patternHovered, setPatternHovered] = useState(false);
  const [selectedPatterns, setSelectedPatterns] = useState([]);
  // Remonte le bouton sur une page restaurée du bfcache après un window.location
  // (guides, App Router) : il y serait resté figé en chargement.
  const [buttonKey, setButtonKey] = useState(0);

  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) setButtonKey((k) => k + 1);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(divRefs).forEach((key) => {
        if (divRefs[key].current && !divRefs[key].current.contains(event.target)) {
          setClicked((prev) => ({ ...prev, [key]: false }));
        }
      });
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleClick = (key) => {
    setClicked((prev) => ({ ...prev, [key]: true }));
  };

  // Skin variants
  const skin = {
    lvl0: 'flex mx-auto w-full px-6 lg:px-0',
    lvl1: 'flex justify-center w-full lg:pb-0',
    lvl2: 'z-10 bg-white shadow-lg rounded-xl w-full lg:w-[800px]',
  };
  if (isCms) {
    skin.lvl0 = 'pt-1 lg:pt-2 flex mx-auto w-full';
    skin.lvl1 = 'flex justify-center w-full pb-0';
    skin.lvl2 = 'z-10 bg-white shadow-lg rounded-2xl w-full p-0 mx-6 lg:mx-0 lg:w-[780px]';
  }
  if (isPopin) {
    skin.lvl2 = 'w-screen h-dvh flex justify-center lg:h-fit lg:shadow-2xl lg:rounded-2xl sm:w-full md:min-w-450 md:max-w-[800px] lg:w-[850px] pt-20 lg:pt-0';
  }

  // Popin (header, pages internes) : même pilule blanche que le hero de la homepage.
  if (isPopin) {
    return (
      <div className="flex min-h-dvh w-screen flex-col bg-white px-4 pb-8 pt-20 lg:min-h-0 lg:w-auto lg:bg-transparent lg:px-0 lg:pb-0 lg:pt-0">
        {moreHtml.title}
        <div className="flex justify-center">
          <HeroSearch cityDefault={cityDefault} onSearch={(routerData) => (onClose ? onClose(routerData) : navigateToSearch({ router, routerData, langCurrent }))} />
        </div>
        {moreHtml.footer || null}
      </div>
    );
  }

  async function gotoSearch() {
    setOrEvent('search');
    const routerData = await computeSearchRoute({
      refs, selectedPatterns, cityDefault, langCurrent,
    });
    if (!routerData) return;

    if (isPopin && onClose) {
      onClose(routerData);
    } else {
      // Button garde son loader (et bloque les clics) tant que cette promesse court.
      await navigateToSearch({ router, routerData, langCurrent });
    }
  }

  const getFieldClassName = (field, nextField, nextHovered, baseWidth = 'lg:min-w-36', activeWidth = 'lg:min-w-56') => {
    const isActive = clicked[field];
    const hideAfter = clicked[nextField] || nextHovered || isActive;
    const widthClass = isActive ? activeWidth : baseWidth;
    return `${isActive ? 'lg:z-10 lg:bg-black lg:shadow-light lg:after:w-0' : 'lg:hover:bg-black/50'} ${hideAfter ? 'lg:after:w-0' : 'lg:after:w-[1px]'} ${widthClass} rounded-[10px] lg:hover:after:w-0 transition-all duration-300 ease-in-out relative lg:after:absolute lg:after:right-0 lg:after:top-4 lg:after:bottom-4 lg:after:bg-purple-4/30 lg:hover:rounded-[10px]`;
  };

  return (
    <div className={skin.lvl0}>
      <div className={skin.lvl1}>
        <div className={skin.lvl2}>
          <div className="hidden mx-6 mt-14 h-12 modal-icon">
            <Icon className="w-12 h-12 text-white fill-current" name="logo-officeriders-min" />
          </div>
          {moreHtml.title}
          <div className="w-full rounded-t-xl md:rounded-b-xl md:rounded-t-none">
            <div className={`${clicked.calendar || clicked.hubs ? 'lg:bg-purple-2' : ''} lg:p-[2px] lg:bg-linear-to-r lg:from-purple lg:to-red rounded-xl`}>
              <div className="grid items-center lg:rounded-[10px] justify-center grid-cols-1 p-3 lg:p-0 bg-[#271E38] lg:grid-flow-col rounded-xl gap-x-0">

                {/* Hubs Field */}
                <div
                  ref={divRefs.hubs}
                  className={`${getFieldClassName('hubs', 'calendar', calendarHovered, 'lg:min-w-24', 'lg:min-w-44')} cursor-pointer`}
                  onClick={() => handleClick('hubs')}
                >
                  <Hubs
                    city={cityDefault}
                    ref={refs.refHubs}
                    onSelected={(hubselected) => {
                      if (hubselected) {
                        setClicked((prev) => ({ ...prev, hubs: false }));
                        handleClick('calendar');
                        setCalendarHovered(true);
                        refs.refCal.current?.setIsOpenModal?.(true);
                      }
                    }}
                  />
                </div>

                {/* Calendar Field */}
                <div
                  ref={divRefs.calendar}
                  className={`${getFieldClassName('calendar', 'pattern', patternHovered)} cursor-pointer`}
                  onClick={() => handleClick('calendar')}
                  onMouseEnter={() => setCalendarHovered(true)}
                  onMouseLeave={() => setCalendarHovered(false)}
                >
                  <Calendar forwardRef={refs.refCal} />
                </div>

                {/* Pattern/Keywords Field */}
                <div
                  ref={divRefs.pattern}
                  className={`${clicked.pattern ? 'lg:z-10 lg:bg-black lg:shadow-light lg:min-w-80' : 'lg:hover:bg-black/50 lg:min-w-64'} rounded-[10px] transition-all duration-300 ease-in-out relative lg:hover:rounded-[10px] cursor-text`}
                  onClick={() => {
                    handleClick('pattern');
                    patternSelectRef.current?.focus();
                  }}
                  onMouseEnter={() => setPatternHovered(true)}
                  onMouseLeave={() => setPatternHovered(false)}
                >
                  <div className="mb-2 w-full rounded-lg border border-purple/50 shadow-light lg:border-none lg:shadow-none lg:rounded-none lg:mb-0 lg:border-b-0">
                    <label className="text-left block text-[14px] text-purple-4 pt-2 lg:pt-3 pb-0 px-4">
                      {t('search:filter.patterns.title')}
                      <Icon name="sparkles" className="inline-block pb-0.5 ml-1 size-5 text-red" />
                    </label>
                    <div className="bg-transparent lg:bottom-[1px] lg:h-12 pt-0 pb-2 lg:pt-1.5 lg:pb-3 px-4 md:text-md block w-full border-none focus:ring-0">
                      <AsyncSelect
                        ref={patternSelectRef}
                        isMulti
                        loadOptions={loadPatternOptions}
                        placeholder={t('search:filter.patterns.placeholder')}
                        onChange={setSelectedPatterns}
                        value={selectedPatterns}
                        noOptionsMessage={() => t('search:filter.patterns.options')}
                        classNames={selectClassNames}
                        styles={selectStyles}
                      />
                    </div>
                  </div>
                </div>

                {/* Search Button */}
                <div className="shrink lg:w-14 lg:mx-3.5 lg:my-0">
                  <Button
                    key={buttonKey}
                    aria-label="search"
                    name="search"
                    className="flex justify-center items-center py-3 w-full text-lg text-white rounded-lg shrink lg:size-14 bg-red hover:bg-red-dark"
                    onClick={gotoSearch}
                  >
                    <Icon name="search" className="w-5 text-white" />
                    <span className="inline ml-2 leading-none lg:hidden">{t('core:search')}</span>
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="mx-4 md:mx-6 w-full lg:mx-0 lg:w-[200px] relative top-0 lg:top-6" />
            </div>
          </div>
        </div>
      </div>
      {moreHtml.footer || null}
    </div>
  );
}

export default hocTranslateServerClient(Search);
